import React, { useState } from 'react';
import {
  Shield, Lock, MapPin, Sun, Moon, Eye, EyeOff,
  ChevronRight, CheckCircle, X, ArrowRight
} from 'lucide-react';
import { packages } from '../data/mockData';
import PackageCard from './PackageCard';
import PackageDetailModal from './PackageDetailModal';

const DEMO_EMAIL = 'jamaah@umrohloka.com';
const DEMO_PASSWORD = 'password123';

export default function LandingPage({ theme, toggleTheme, onLogin }) {
  const [showModal, setShowModal] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  const isDark = theme === 'dark';

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setTimeout(() => {
      if (email === DEMO_EMAIL && password === DEMO_PASSWORD) {
        onLogin({ name: 'Budi Santoso', email });
      } else {
        setError('Email atau password salah. Gunakan kredensial demo di bawah.');
      }
      setLoading(false);
    }, 900);
  };

  const fillDemo = () => {
    setEmail(DEMO_EMAIL);
    setPassword(DEMO_PASSWORD);
    setError('');
  };

  const features = [
    {
      icon: Shield,
      color: '#00C9B1',
      title: 'Trust Score',
      subtitle: 'Transparan & Terverifikasi',
      desc: 'Setiap travel agent dinilai secara objektif dari 15+ parameter. AI kami memantau kinerja secara real-time untuk menjamin kualitas.',
    },
    {
      icon: Lock,
      color: '#F5A623',
      title: 'Smart Escrow',
      subtitle: 'Dana 100% Terlindungi',
      desc: 'Dana Anda disimpan di rekening escrow berlisensi OJK. Hanya dicairkan setelah setiap milestone perjalanan terpenuhi.',
    },
    {
      icon: MapPin,
      color: '#00C9B1',
      title: 'Journey Tracker',
      subtitle: 'Pantau Real-time',
      desc: 'Lacak setiap tahap perjalanan dari pendaftaran hingga kepulangan. Notifikasi otomatis di setiap milestone.',
    },
  ];

  const stats = [
    { value: '50K+', label: 'Jamaah Terdaftar' },
    { value: '99.8%', label: 'Kepuasan Jamaah' },
    { value: 'Rp 2.3T+', label: 'Dana Diamankan Escrow' },
    { value: '250+', label: 'Travel Agent Terverifikasi' },
  ];

  return (
    <div className={`min-h-screen font-poppins ${isDark ? 'bg-[#0E0E12] text-white' : 'bg-[#F7FAFC] text-[#1A202C]'}`}>
      {/* ─── NAVBAR ─── */}
      <nav
        data-testid="landing-navbar"
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${isDark ? 'glass-dark' : 'glass-light'}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div data-testid="navbar-logo" className="flex items-center">
            <span className="text-2xl font-black tracking-tight">
              <span className="text-[#00C9B1]">UMROH</span>
              <span className={isDark ? 'text-white' : 'text-[#1A202C]'}>LOKA</span>
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="theme-toggle-landing"
              onClick={toggleTheme}
              className={`p-2 rounded-xl transition-colors ${isDark ? 'text-gray-400 hover:text-[#00C9B1] hover:bg-white/5' : 'text-gray-500 hover:text-[#00C9B1] hover:bg-gray-100'}`}
              title="Toggle Theme"
            >
              {isDark ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            <button
              data-testid="login-cta-btn"
              onClick={() => setShowModal(true)}
              className="px-5 py-2 bg-[#F5A623] text-[#0E0E12] font-semibold rounded-xl hover:brightness-110 transition-all hover:-translate-y-0.5 shadow-lg shadow-[#F5A623]/20 text-sm"
            >
              Masuk / Daftar
            </button>
          </div>
        </div>
      </nav>

      {/* ─── HERO ─── */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
        {/* Video Background */}
        <div className="absolute inset-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            poster="https://images.pexels.com/photos/32290182/pexels-photo-32290182.jpeg?auto=compress&cs=tinysrgb&w=1920&q=80"
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source
              src="https://rahmahtravel.id/videos/video-makkah.mp4"
              type="video/mp4"
            />
            {/* Fallback gambar statis jika browser tidak support video */}
            <img
              src="https://images.pexels.com/photos/32290182/pexels-photo-32290182.jpeg?auto=compress&cs=tinysrgb&w=1920&q=80"
              alt="Ka'bah Makkah"
              className="w-full h-full object-cover"
            />
          </video>
          <div className="absolute inset-0 bg-[#0E0E12]/45" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0E0E12]/10 via-transparent to-[#0E0E12]" />
        </div>

        {/* Floating glow orbs */}
        <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-[#00C9B1]/8 blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/3 right-1/4 w-48 h-48 rounded-full bg-[#F5A623]/8 blur-3xl pointer-events-none" />

        {/* Content */}
        <div className="relative z-10 text-center max-w-4xl mx-auto px-4 py-20">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00C9B1]/10 border border-[#00C9B1]/25 rounded-full mb-8 animate-fade-in-up">
            <span className="w-2 h-2 rounded-full bg-[#00C9B1] animate-live-pulse" />
            <span className="text-[#00C9B1] text-sm font-medium">Ekosistem Umrah Terpercaya #1 Indonesia</span>
          </div>

          {/* Headline */}
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-tight mb-6 animate-fade-in-up delay-100">
            Where Trust
            <br />
            <span className="gradient-text-gold">Meets</span>{' '}
            <span className="text-white">Worship</span>
          </h1>

          {/* Subtitle */}
          <p className="text-lg text-gray-300 max-w-2xl mx-auto mb-10 leading-relaxed animate-fade-in-up delay-200">
            Platform Umrah pertama di Indonesia dengan{' '}
            <span className="text-[#00C9B1] font-semibold">Smart Escrow Technology</span> dan{' '}
            <span className="text-[#00C9B1] font-semibold">Journey Tracker</span> real-time.
            Dana Anda aman, perjalanan Anda terpantau.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16 animate-fade-in-up delay-300">
            <button
              data-testid="hero-cta-btn"
              onClick={() => setShowModal(true)}
              className="px-8 py-4 bg-[#F5A623] text-[#0E0E12] font-bold text-base rounded-2xl hover:brightness-110 transition-all hover:-translate-y-1 shadow-xl shadow-[#F5A623]/25 gold-glow flex items-center justify-center gap-2"
            >
              Mulai Sekarang <ArrowRight size={18} />
            </button>
            <button
              data-testid="hero-learn-more-btn"
              onClick={() => document.getElementById('katalog-paket')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-4 border border-[#00C9B1]/40 text-[#00C9B1] font-semibold text-base rounded-2xl hover:bg-[#00C9B1]/10 transition-all hover:-translate-y-0.5 flex items-center justify-center gap-2"
            >
              Lihat Paket Umroh <ChevronRight size={18} />
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-3xl mx-auto animate-fade-in-up delay-400">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-2xl sm:text-3xl font-black text-[#F5A623]">{stat.value}</div>
                <div className="text-gray-400 text-xs sm:text-sm mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FEATURES ─── */}
      <section data-testid="features-section" className={`py-24 px-4 ${isDark ? 'bg-[#0E0E12]' : 'bg-[#F7FAFC]'}`}>
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <div className={`inline-block text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-4 ${isDark ? 'text-[#00C9B1] bg-[#00C9B1]/10' : 'text-[#00C9B1] bg-[#00C9B1]/10'}`}>
              Mengapa UMROHLOKA?
            </div>
            <h2 className={`text-3xl sm:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-[#1A202C]'}`}>
              Teknologi untuk Ibadah yang Lebih{' '}
              <span className="text-[#00C9B1]">Tenang</span>
            </h2>
            <p className={`max-w-2xl mx-auto ${isDark ? 'text-gray-400' : 'text-[#4A5568]'}`}>
              Kami menggabungkan teknologi terdepan dengan pemahaman mendalam tentang kebutuhan jamaah Indonesia
            </p>
          </div>

          {/* Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {features.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <div
                  key={i}
                  data-testid={`feature-card-${i}`}
                  className={`card-hover rounded-2xl p-8 border ${isDark
                    ? 'bg-[#1A1A24] border-[#2D3748] hover:border-[#00C9B1]/30'
                    : 'bg-white border-[#E2E8F0] hover:border-[#00C9B1]/30'
                  } transition-all`}
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-6"
                    style={{ background: `${feature.color}15` }}
                  >
                    <Icon size={24} color={feature.color} strokeWidth={1.5} />
                  </div>
                  <div className="text-xs font-semibold tracking-widest uppercase mb-2" style={{ color: feature.color }}>
                    {feature.subtitle}
                  </div>
                  <h3 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-[#1A202C]'}`}>
                    {feature.title}
                  </h3>
                  <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-[#4A5568]'}`}>
                    {feature.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS ─── */}
      <section className={`py-20 px-4 ${isDark ? 'bg-[#1A1A24]' : 'bg-white'}`}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className={`text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-[#1A202C]'}`}>
            Perjalanan Umroh Lebih <span className="text-[#F5A623]">Mudah & Aman</span>
          </h2>
          <p className={`mb-12 ${isDark ? 'text-gray-400' : 'text-[#4A5568]'}`}>
            Hanya 4 langkah untuk memulai perjalanan ibadah Anda
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Pilih Paket', desc: 'Browse ratusan paket dari travel agent terverifikasi' },
              { step: '02', title: 'Bayar via Escrow', desc: 'Dana aman tersimpan di rekening escrow berlisensi' },
              { step: '03', title: 'Track Perjalanan', desc: 'Pantau setiap tahap dari visa hingga keberangkatan' },
              { step: '04', title: 'Berangkat Tenang', desc: 'Terbang ke Baitullah dengan penuh keyakinan' },
            ].map((item) => (
              <div key={item.step} className={`rounded-2xl p-6 text-center ${isDark ? 'bg-[#0E0E12]' : 'bg-[#F7FAFC]'}`}>
                <div className="text-3xl font-black text-[#00C9B1]/30 mb-3">{item.step}</div>
                <div className={`font-bold mb-2 ${isDark ? 'text-white' : 'text-[#1A202C]'}`}>{item.title}</div>
                <div className={`text-xs leading-relaxed ${isDark ? 'text-gray-500' : 'text-[#718096]'}`}>{item.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── KATALOG PAKET ─── */}
      <section id="katalog-paket" data-testid="catalog-section" className={`py-20 px-4 ${isDark ? 'bg-[#0E0E12]' : 'bg-[#F7FAFC]'}`}>
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <div className={`inline-block text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-4 ${isDark ? 'text-[#F5A623] bg-[#F5A623]/10' : 'text-[#D4881A] bg-[#F5A623]/10'}`}>
              Pilihan Terpopuler
            </div>
            <h2 className={`text-3xl sm:text-4xl font-bold mb-3 ${isDark ? 'text-white' : 'text-[#1A202C]'}`}>
              Paket Umroh <span className="text-[#F5A623]">Terpercaya</span>
            </h2>
            <p className={`max-w-xl mx-auto text-base ${isDark ? 'text-gray-400' : 'text-[#4A5568]'}`}>
              Semua paket dari travel agent berverifikasi dan dilindungi Smart Escrow OJK
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex gap-2 justify-center flex-wrap mb-8">
            {[
              { id: 'all', label: 'Semua' },
              { id: 'ramadan', label: 'Ramadan' },
              { id: 'regular', label: 'Reguler' },
              { id: 'vip', label: 'VIP' },
              { id: 'keluarga', label: 'Keluarga' },
            ].map(cat => (
              <button
                key={cat.id}
                data-testid={`landing-category-${cat.id}`}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-5 py-2 rounded-xl text-sm font-medium transition-all ${
                  activeCategory === cat.id
                    ? 'bg-[#00C9B1] text-[#0E0E12]'
                    : isDark
                    ? 'bg-[#1A1A24] border border-[#2D3748] text-gray-400 hover:border-[#00C9B1]/40'
                    : 'bg-white border border-[#E2E8F0] text-gray-500 hover:border-[#00C9B1]/40'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Package Grid */}
          <div data-testid="landing-packages-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {packages
              .filter(p => activeCategory === 'all' || p.category === activeCategory)
              .map(pkg => (
                <PackageCard
                  key={pkg.id}
                  pkg={pkg}
                  onViewDetail={setSelectedPackage}
                />
              ))}
          </div>

          {/* Bottom CTA */}
          <div className={`rounded-3xl p-8 text-center border ${isDark ? 'bg-[#1A1A24] border-[#2D3748]' : 'bg-white border-[#E2E8F0]'}`}>
            <p className={`text-base mb-2 font-medium ${isDark ? 'text-gray-300' : 'text-[#2D3748]'}`}>
              Siap memesan paket impian Anda?
            </p>
            <p className={`text-sm mb-6 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Masuk atau daftar untuk memesan dengan perlindungan Smart Escrow
            </p>
            <button
              data-testid="catalog-cta-login-btn"
              onClick={() => setShowModal(true)}
              className="px-8 py-4 bg-[#F5A623] text-[#0E0E12] font-bold rounded-2xl hover:brightness-110 transition-all hover:-translate-y-1 shadow-lg shadow-[#F5A623]/25 inline-flex items-center gap-2"
            >
              Masuk & Pesan Sekarang <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* ─── TRUST SECTION ─── */}
      <section className={`py-20 px-4 ${isDark ? 'bg-[#0E0E12]' : 'bg-[#F7FAFC]'}`}>
        <div className="max-w-4xl mx-auto">
          <div className={`rounded-3xl p-10 text-center border ${isDark ? 'bg-gradient-to-br from-[#1A1A24] to-[#0E0E12] border-[#2D3748]' : 'bg-white border-[#E2E8F0]'}`}>
            <div className="w-16 h-16 bg-[#00C9B1]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Shield size={32} color="#00C9B1" strokeWidth={1.5} />
            </div>
            <h2 className={`text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-[#1A202C]'}`}>
              Bergabung dengan <span className="text-[#F5A623]">50.000+</span> Jamaah
            </h2>
            <p className={`mb-8 max-w-lg mx-auto ${isDark ? 'text-gray-400' : 'text-[#4A5568]'}`}>
              Ribuan jamaah Indonesia telah mempercayakan perjalanan ibadah mereka kepada UMROHLOKA.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              {['OJK Berlisensi', 'PPIU Resmi', 'ISO 27001 Certified', 'Kemenag RI Terdaftar'].map(badge => (
                <span key={badge} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${isDark ? 'bg-[#00C9B1]/10 text-[#00C9B1]' : 'bg-[#00C9B1]/10 text-[#00897B]'}`}>
                  <CheckCircle size={14} /> {badge}
                </span>
              ))}
            </div>
            <button
              data-testid="cta-bottom-btn"
              onClick={() => setShowModal(true)}
              className="px-8 py-4 bg-[#F5A623] text-[#0E0E12] font-bold rounded-2xl hover:brightness-110 transition-all hover:-translate-y-1 shadow-lg shadow-[#F5A623]/25 inline-flex items-center gap-2"
            >
              Daftar Gratis Sekarang <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className={`py-10 px-4 border-t ${isDark ? 'bg-[#0E0E12] border-[#2D3748]' : 'bg-white border-[#E2E8F0]'}`}>
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-xl font-black">
            <span className="text-[#00C9B1]">UMROH</span>
            <span className={isDark ? 'text-white' : 'text-[#1A202C]'}>LOKA</span>
          </span>
          <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-[#718096]'}`}>
            © 2026 UMROHLOKA. All rights reserved. Berlisensi OJK & Kemenag RI.
          </p>
          <div className="flex gap-4">
            {['Privasi', 'Syarat', 'Bantuan'].map(link => (
              <a key={link} href="#" className={`text-sm hover:text-[#00C9B1] transition-colors ${isDark ? 'text-gray-500' : 'text-[#718096]'}`}>{link}</a>
            ))}
          </div>
        </div>
      </footer>

      {/* ─── PACKAGE DETAIL MODAL ─── */}
      {selectedPackage && (
        <PackageDetailModal
          pkg={selectedPackage}
          isAuthenticated={false}
          onClose={() => setSelectedPackage(null)}
          onLoginRequired={() => {
            setSelectedPackage(null);
            setShowModal(true);
          }}
        />
      )}

      {/* ─── LOGIN MODAL ─── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
          <div
            className="absolute inset-0 bg-[#0E0E12]/80 backdrop-blur-sm"
            onClick={() => setShowModal(false)}
          />
          <div
            data-testid="login-modal"
            className={`relative w-full max-w-md rounded-2xl p-8 border shadow-2xl animate-fade-in-up ${isDark
              ? 'bg-[#1A1A24] border-[#2D3748]'
              : 'bg-white border-[#E2E8F0]'
            }`}
          >
            {/* Close */}
            <button
              data-testid="modal-close-btn"
              onClick={() => setShowModal(false)}
              className={`absolute top-4 right-4 p-2 rounded-xl transition-colors ${isDark ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'}`}
            >
              <X size={18} />
            </button>

            {/* Logo */}
            <div className="text-center mb-8">
              <span className="text-2xl font-black tracking-tight">
                <span className="text-[#00C9B1]">UMROH</span>
                <span className={isDark ? 'text-white' : 'text-[#1A202C]'}>LOKA</span>
              </span>
              <p className={`mt-2 text-sm ${isDark ? 'text-gray-400' : 'text-[#4A5568]'}`}>
                Masuk ke akun Anda
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email */}
              <div>
                <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-[#2D3748]'}`}>
                  Email
                </label>
                <input
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  placeholder="email@contoh.com"
                  className={`w-full px-4 py-3 rounded-xl text-sm border outline-none transition-colors focus:border-[#00C9B1] ${isDark
                    ? 'bg-[#242430] border-[#2D3748] text-white placeholder-gray-600 focus:bg-[#242430]'
                    : 'bg-[#F7FAFC] border-[#E2E8F0] text-[#1A202C] placeholder-gray-400'
                  }`}
                  required
                />
              </div>

              {/* Password */}
              <div>
                <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-[#2D3748]'}`}>
                  Password
                </label>
                <div className="relative">
                  <input
                    data-testid="login-password-input"
                    type={showPass ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(''); }}
                    placeholder="••••••••"
                    className={`w-full px-4 py-3 pr-12 rounded-xl text-sm border outline-none transition-colors focus:border-[#00C9B1] ${isDark
                      ? 'bg-[#242430] border-[#2D3748] text-white placeholder-gray-600'
                      : 'bg-[#F7FAFC] border-[#E2E8F0] text-[#1A202C] placeholder-gray-400'
                    }`}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}
                  >
                    {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Error */}
              {error && (
                <div data-testid="login-error" className="text-red-400 text-sm bg-red-400/10 border border-red-400/20 rounded-xl px-4 py-3">
                  {error}
                </div>
              )}

              {/* Submit */}
              <button
                data-testid="login-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full py-3.5 bg-[#F5A623] text-[#0E0E12] font-bold rounded-xl hover:brightness-110 transition-all hover:-translate-y-0.5 shadow-lg shadow-[#F5A623]/20 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? 'Memverifikasi...' : 'Masuk ke Akun'}
              </button>
            </form>

            <p className={`text-center text-sm mt-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Belum punya akun?{' '}
              <span className="text-[#00C9B1] cursor-pointer hover:underline font-medium">
                Daftar Gratis
              </span>
            </p>

            {/* Demo Credentials */}
            <div
              className={`mt-6 p-4 rounded-xl border cursor-pointer hover:border-[#00C9B1]/40 transition-colors ${isDark
                ? 'bg-[#0E0E12] border-[#2D3748]'
                : 'bg-[#F7FAFC] border-[#E2E8F0]'
              }`}
              onClick={fillDemo}
              data-testid="demo-credentials"
              title="Klik untuk isi otomatis"
            >
              <p className="text-xs font-semibold text-[#00C9B1] uppercase tracking-wider mb-2">
                Demo Credentials (klik untuk isi otomatis)
              </p>
              <div className={`text-xs space-y-1 ${isDark ? 'text-gray-400' : 'text-[#4A5568]'}`}>
                <div>Email: <span className="font-mono font-medium">{DEMO_EMAIL}</span></div>
                <div>Password: <span className="font-mono font-medium">{DEMO_PASSWORD}</span></div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
